// The ORDER's P3-D mutations (Module 09 §5): the direct order's `POST
// /v1/orders` and `POST /v1/orders/{id}/decide` (slice P3-D PR-D1, D-283),
// the line cancel `POST /v1/orders/{id}/lines/{lineId}/cancel` (PR-D2, D-285)
// and the office's delivered door `POST /v1/orders/{id}/mark-delivered`
// (PR-D3, D-286), plus PR-D4's two (D-288): the audited status override
// `POST /v1/orders/{id}/override-status` and the delivery note's re-render
// `POST /v1/shipments/{id}/delivery-note` — TanStack Query over the generated api-client, the exact posture of
// hooks/quote-queries.ts: doctrine 3 means apps/web talks to the API only
// through `@assessly/api-client`, every mutation carries a fresh
// Idempotency-Key, and every mutation invalidates exactly the reads it can
// have moved.
//
// A THIRD FILE, for the same people-not-code reason quote-queries.ts gives for
// being a second one: the PR-D1 pair is imported by TWO screens built in
// parallel by two agents (the §4.4 composer's order mode and the §4.6 inbox's
// fourth decidable kind), and a hook that lives in neither agent's file cannot
// be overwritten by either. The query KEYS stay in quote-queries.ts
// (`quoteKeys`) — one owner for the cache's shape, never two.
//
// WHAT `useDecideOrder` CLOSES: quote-queries.ts's header recorded that the
// direct-order decide was "NOT registered" and that its §4.6 card rendered
// informational with the slice named (D-088 — no button into a route that
// 404s). The route is on the wire now, so the card gets its Approve / Reject.
//
// WHERE THE CANCEL FAMILY LIVES: `useCancelOrder` predates this file and stays
// in quote-queries.ts (moving it would churn two screens' imports to no end);
// `useCancelOrderLine` lands HERE because this is the file the order writes
// have accrued in since PR-D1, and the §4.7 detail imports both — one code
// (`sales.order.cancel`), two doors, the same invalidation set plus the §4.6
// inbox (the last line's auto-cancel withdraws a credit card from the feed).
//
// P3-D PR-D5 (D-289) adds two things here. The create now answers the 201's
// `X-Sales-Warning` header beside the order (the duplicate customer-PO warning,
// ruling 4), with a one-slot hand-over that carries it to the order detail the
// composer lands on. And `useRequoteOrder` posts `POST /v1/orders/{id}/requote`
// (rulings 6-8), the order's counterpart to the quote's Duplicate: it mints a
// QUOTE, so it invalidates the quote lists and nothing on the order.

import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { components } from "@assessly/api-client";
import { client } from "../../../../lib/api";
import { newIdempotencyKey, unwrap } from "../../../../lib/request";
import {
  invalidateDealReadsFromDocument,
  quoteKeys,
  type OrderDetail,
  type QuoteDuplicateResponse,
} from "./quote-queries";

type S = components["schemas"];

export type OrderCreateRequest = S["OrderCreateRequest"];
export type OrderDecideRequest = S["OrderDecideRequest"];
export type OrderOverrideStatusRequest = S["OrderOverrideStatusRequest"];
export type OrderRequoteRequest = S["OrderRequoteRequest"];

/** What `POST /v1/orders` answered: the order, and the 201's typed warning
 * code when the server sent one. */
export interface CreatedOrder {
  readonly order: OrderDetail;
  /** The `X-Sales-Warning` header off the 201 — a TYPED code
   * (`DUPLICATE_PO_REF`), localized by the client (D-225, the §9.13 pattern the
   * customer create set). Read BEFORE unwrap, because unwrap surrenders the
   * Response on the throw path only. An idempotent replay does not carry it
   * (the interceptor replays the body, not the headers), which the route's
   * contract states. */
  readonly warning: string | undefined;
}

/** §3.6 / §5 — place a direct order. Lands `confirmed` or `pending_approval`
 * (the server decides; the composer never computes a price it then sends
 * back — the §4.4 rule, unchanged for the order document). Invalidates the
 * §4.7 list AND the §4.6 inbox: a `pending_approval` order is a new card. */
export function useCreateOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: OrderCreateRequest): Promise<CreatedOrder> => {
      const res = await client.POST("/v1/orders", {
        params: { header: { "Idempotency-Key": newIdempotencyKey() } },
        body,
      });
      const warning = res.response.headers.get("X-Sales-Warning") ?? undefined;
      const order = unwrap(res, "Could not place the order");
      return { order, warning };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: quoteKeys.orderLists() });
      void qc.invalidateQueries({ queryKey: quoteKeys.reviewLists() });
    },
  });
}

// ---------------------------------------------------------------------------
// The create's WARNING, handed from the composer to the order it placed
// ---------------------------------------------------------------------------

/**
 * ONE SLOT, for the reason hooks/quote-queries.ts gives for the duplicate
 * diff's: the header is stated once, in the 201, about a document the
 * composer is about to navigate to, and the query cache would make the hand-
 * over a race between the router and a garbage-collection timer. One press
 * places one order, so a second hand-over before the first is claimed has
 * superseded it.
 */
let pendingOrderWarning: { readonly orderId: string; readonly code: string } | undefined;

export function handOverOrderWarning(orderId: string, code: string): void {
  pendingOrderWarning = { orderId, code };
}

/** Take the warning waiting for THIS order, once. A second read answers
 * `undefined`, so a re-render cannot re-raise a notice the reader dismissed,
 * and a later visit to the same order shows nothing. */
export function claimOrderWarning(orderId: string): string | undefined {
  if (pendingOrderWarning?.orderId !== orderId) return undefined;
  const claimed = pendingOrderWarning.code;
  pendingOrderWarning = undefined;
  return claimed;
}

/**
 * The arrival side: claim the warning for the order now on screen, exactly
 * once per order. Keyed on `orderId` in a ref (not on mount), the duplicate
 * diff's arrival rule: a StrictMode double-invoked effect sees the order
 * already claimed and leaves the notice alone instead of clearing it.
 *
 * The claimed code is stored WITH the order it was claimed for, and read back
 * only while that order is the one on screen. The router reuses the detail
 * screen when the reader moves to another order, and the effect that claims
 * for the new order runs after the first render under the new id; state keyed
 * by nothing would render the previous order's notice for that one frame.
 */
export function useArrivingOrderWarning(orderId: string): {
  code: string | undefined;
  dismiss: () => void;
} {
  const claimedFor = useRef<string | undefined>(undefined);
  const [arrived, setArrived] = useState<
    { readonly orderId: string; readonly code: string | undefined } | undefined
  >(undefined);
  useEffect(() => {
    if (claimedFor.current === orderId) return;
    claimedFor.current = orderId;
    setArrived({ orderId, code: claimOrderWarning(orderId) });
  }, [orderId]);
  return {
    code: arrived?.orderId === orderId ? arrived.code : undefined,
    dismiss: () => {
      setArrived(undefined);
    },
  };
}

/**
 * §5 `POST /v1/orders/{id}/requote` (P3-D PR-D5, D-289 rulings 6-8) — a new
 * DRAFT quote from the order's surviving lines, repriced at today's lists, with
 * no fulfilment plan carried. The response is the quote duplicate's body, and
 * the caller hands its `repriced` / `dropped` diff to the composer the way the
 * quote's Duplicate does. Invalidates the quote lists only: the order is read,
 * never written. The route requires an Idempotency-Key; this hook mints a
 * FRESH one on every `mutationFn` call and never resends a request, so the key
 * does not turn a second press into a replay: a second press is a second
 * request with a new key, and it makes a second quote. That is why the button
 * refuses a second press until the first settles (`RequoteOrderButton` in
 * screens/order-detail.tsx).
 */
export function useRequoteOrder(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: OrderRequoteRequest): Promise<QuoteDuplicateResponse> => {
      const res = await client.POST("/v1/orders/{id}/requote", {
        params: { path: { id: orderId }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body,
      });
      return unwrap(res, "Could not make a quote from the order");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: quoteKeys.quoteLists() }),
  });
}

/** §3.6 decide (D-231's in-module review, on a DIRECT order). Invalidates the
 * document, the list and the inbox: an approved order's tracking card and
 * confirmed timeline appear the moment it lands, and the card leaves the
 * inbox either way. The `useDecideQuote` shape, pointed at the order. */
export function useDecideOrder(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: OrderDecideRequest) => {
      const res = await client.POST("/v1/orders/{id}/decide", {
        params: { path: { id: orderId }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body,
      });
      return unwrap(res, "Could not record the decision");
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: quoteKeys.order(orderId) });
      void qc.invalidateQueries({ queryKey: quoteKeys.orderLists() });
      void qc.invalidateQueries({ queryKey: quoteKeys.reviewLists() });
      // A deal reads its won order's status (E2-1).
      invalidateDealReadsFromDocument(qc);
    },
  });
}

/** §5 `POST /v1/orders/{id}/lines/{lineId}/cancel` (PR-D2, D-285) — one line
 * leaves a confirmed order: its module-06 holds are released, the header
 * totals are recomputed over the lines that remain, and when it was the last
 * non-cancelled line the service runs the WHOLE-ORDER cancel instead, with
 * this reason verbatim (§9.18). The response is the §4.7 detail either way,
 * so the screen re-renders from it rather than guessing which path ran.
 *
 * Invalidates the document, the §4.7 list AND the §4.6 inbox — the auto-cancel
 * withdraws a `blocked_credit` order's card from the feed, exactly as the
 * order cancel's own hook would need to (it invalidates two of the three; the
 * inbox is the line door's addition and is stated rather than assumed). A
 * FRESH key per press: two line cancels from one screen never collide on the
 * interceptor's `(tenant, key)` store, and the URL carries the `lineId`, so a
 * replayed key against a different line is the conflict it should be. */
export function useCancelOrderLine(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ lineId, reason }: { lineId: string; reason: string }) => {
      const res = await client.POST("/v1/orders/{id}/lines/{lineId}/cancel", {
        params: {
          path: { id: orderId, lineId },
          header: { "Idempotency-Key": newIdempotencyKey() },
        },
        body: { reason },
      });
      return unwrap(res, "Could not cancel the line");
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: quoteKeys.order(orderId) });
      void qc.invalidateQueries({ queryKey: quoteKeys.orderLists() });
      void qc.invalidateQueries({ queryKey: quoteKeys.reviewLists() });
      // The last live line's cancel cancels the whole order, and a deal that
      // order won stays won with its order cancelled (E2-1).
      invalidateDealReadsFromDocument(qc);
    },
  });
}

/** §3.6 `shipped → delivered` / §5 `POST /v1/orders/{id}/mark-delivered`
 * (PR-D3, D-286 ruling 14) — the office's door on the CARRIER's word. Delivery
 * is at order grain: the service stamps every `shipped` shipment `delivered`
 * (`delivered_by: carrier`), moves the order through its one transition body
 * and answers the §4.7 detail, so the screen re-renders the shipments panel,
 * the status chip and the timeline from the response. The customer's own
 * press on the tracking page is the other door on the same body and the
 * source of truth; this one exists for the manager the carrier rang.
 *
 * Invalidates the document and the §4.7 list — never the §4.6 inbox, which a
 * delivery cannot move (nothing pending survives to `shipped`). No body: the
 * route takes none. A FRESH key per press, the line cancel's rule: a manager
 * who presses twice meets the service's own 409 `ORDER_NOT_SHIPPED` on the
 * second (the order is `delivered` by then), rendered as a refusal rather
 * than replayed as a stale 200. */
export function useMarkOrderDelivered(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await client.POST("/v1/orders/{id}/mark-delivered", {
        params: { path: { id: orderId }, header: { "Idempotency-Key": newIdempotencyKey() } },
      });
      return unwrap(res, "Could not mark the order delivered");
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: quoteKeys.order(orderId) });
      void qc.invalidateQueries({ queryKey: quoteKeys.orderLists() });
    },
  });
}

/** §3.6's audited manual transition / §5 `POST /v1/orders/{id}/override-status`
 * (P3-D PR-D4, D-288). The body is the target and the manager's reason,
 * trimmed; the response is the §4.7 detail after the transition, so the status
 * chip, the timeline and the override's reason re-render from it.
 *
 * Invalidates the document and the §4.7 list — the order's status moved, and
 * the list chips it. Never the §4.6 inbox: nothing pending can be forced (the
 * decide is `pending_approval`'s door). A FRESH key per press, the line
 * cancel's rule: a second press after the first landed meets the server's own
 * 422 STATUS_REGRESSION and says so, rather than replaying a stale 200. */
export function useOverrideOrderStatus(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: OrderOverrideStatusRequest) => {
      const res = await client.POST("/v1/orders/{id}/override-status", {
        params: { path: { id: orderId }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body,
      });
      return unwrap(res, "Could not change the order's status");
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: quoteKeys.order(orderId) });
      void qc.invalidateQueries({ queryKey: quoteKeys.orderLists() });
    },
  });
}

/** §3.7 step 4's re-render / `POST /v1/shipments/{id}/delivery-note` (P3-D
 * PR-D4, D-288 ruling 11) — draws the note a pack could not. The pack stood
 * with `delivery_note_file_id` NULL (a glyph the fonts lack, or the object
 * store down), and this press draws it from the frozen lines. The variable is
 * the SHIPMENT id: one order detail can list several shipments, and the row
 * reads `variables` to know which of them is busy.
 *
 * The response is the SHIPMENT, not the order, so the order detail is
 * invalidated rather than patched: its `shipments[]` summary is what carries
 * the new file id. A shipment that already has a note answers 200 unchanged,
 * so a double press is harmless. */
export function useRenderDeliveryNote(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (shipmentId: string) => {
      const res = await client.POST("/v1/shipments/{id}/delivery-note", {
        params: {
          path: { id: shipmentId },
          header: { "Idempotency-Key": newIdempotencyKey() },
        },
      });
      return unwrap(res, "Could not draw the delivery note");
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: quoteKeys.order(orderId) });
    },
  });
}
